<messages>["./Domain"]</messages>

<!--
================================================================================
  Template (HTML)
================================================================================
-->
<template>
  <base-layout mw1>
    <v-col cols="12">
      <!-- "domain does not exist" alert -->
      <v-alert
        type="error"
        :value="!isLoading && !domain">
        {{ $t ('view.invalidDomain', { domain: domainName }) }}
      </v-alert>

      <!-- domain details -->
      <v-card v-if="domain">
        <v-card-title primary-title class="py-0">
          <div>
            <div>
              <span
                class="text-h5"
                :class="{deletedItemHeadline: domain.currentVersionDeleted}"
                v-text="domain.name"/>
              <template v-if="hasPunycodeName">
                (<span
                  class="cgwng-subheading"
                  v-text="`${domain.namePunycode}`"/>)
              </template>
            </div>
            <div class="cgwng-subheading">
              <registry-type-name :value="domain.registryId" cut-tlds :max-tlds="5"/>
            </div>
          </div>
        </v-card-title>
        <v-tabs
          v-model="activeTab"
          right
          slider-color="primary">
          <!-- current domain data -->
          <v-tab
            key="current"
            v-t="'general.history.current'"
            :disabled="domain.currentVersionDeleted"/>
          <v-tab-item key="current">
            <v-card-text v-if="domain" class="pt-0">
              <domain-data
                :domain="domain"
                :type="type">
                <template
                  v-if="isInquirable"
                  #controls>
                  <i18n :path="`view.includeRemote.${includeRemote}.text`">
                    <template #link>
                      <a
                        v-t="`view.includeRemote.${includeRemote}.link`"
                        @click="includeRemote = !includeRemote"/>
                    </template>
                  </i18n>
                </template>
              </domain-data>
            </v-card-text>
            <v-card-actions v-if="isManageable">
              <v-spacer/>
              <back-button/>
              <v-btn
                v-if="isSyncable"
                v-t="'general.button.sync'"
                @click="syncDomain"/>
              <v-btn
                v-if="isStatusModifyShown && type === TYPES.DOMAIN && isEditable"
                v-t="'general.button.manageStates'"
                :disabled="isStatusModifyDisabled"
                @click="statesDialog=true"/>
              <v-btn
                v-if="isAdmin && type === TYPES.DOMAIN"
                v-t="'general.button.phaseChange'"
                @click="phaseDialog=true"/>
              <v-btn
                v-if="isEditable"
                v-t="'general.button.edit'"
                :disabled="isModifyDisabled"
                :to="{
                  name: type === TYPES.APPLICATION
                    ? 'application.edit.id' : 'domain.edit',
                  params: type === TYPES.APPLICATION
                    ? {id} : {name: domainName}
                }"
                color="primary"/>
            </v-card-actions>
            <states-dialog
              v-if="type === TYPES.DOMAIN && domain.id"
              v-model="statesDialog"
              type="domain"
              :object="domain"
              @success="loadDomainByProps"/>
            <change-phase-dialog
              v-model="phaseDialog"
              :phase-object="domain"
              @changed="loadDomainByProps"/>
          </v-tab-item>

          <!-- historic domain data -->
          <v-tab
            key="historic"
            v-t="'general.history.historic'"/>
          <v-tab-item key="historic">
            <v-card-text class="pt-2">
              <history-panel
                v-for="(data, idx) in historicObjectData"
                :id="'version-' + data.versionId"
                :key="data.versionId"
                :expanded="isHistoryPanelExpanded (idx)"
                :seq-number="idx"
                @expand="assignHistoricDomain (idx)">
                <template #label>
                  <span v-text="idx + 1"/>
                </template>
                <template #start>
                  <span v-text="formatDate (data.from)"/>
                </template>
                <template #end>
                  <span v-text="data.to ? formatDate (data.to) : $t ('general.history.now')"/>
                </template>
                <template #operation>
                  <order-type v-if="data.orderType" :name="data.orderType"/>
                </template>
                <template #content>
                  <domain-data
                    v-if="!data.isLoading && data.domain"
                    history
                    class="ma-0"
                    :domain="data.domain"
                    :type="type"
                    :registry-metadata="registryMetadata"/>
                  <v-progress-linear
                    v-else-if="data.isLoading"
                    indeterminate
                    color="primary"/>
                  <simple-card
                    v-else
                    :title="$t(`general.noData`)"/>
                </template>
              </history-panel>
            </v-card-text>
          </v-tab-item>
        </v-tabs>
      </v-card>
    </v-col>
  </base-layout>
</template>

<!--
================================================================================
  Logic (JavaScript)
================================================================================
-->

<script>
  import {mapGetters, mapMutations, mapActions} from 'vuex'

  import BackButton from '@/app/core/components/BackButton'
  import BaseLayout from '@/app/core/components/BaseLayout'
  import HistoryPanel from '@/app/core/components/HistoryPanel'
  import OrderType from '@/app/core/components/OrderType'
  import SimpleCard from '@/app/core/components/SimpleCard'
  import RegistryTypeName from '@/app/core/components/RegistryTypeName'

  import StatesDialog, {isUpdateProhibited}
    from '@/app/core/components/RegistryObject/StatesDialog'

  import RegistryCommands from '@/app/core/mixins/RegistryCommands'

  import RegistryObjectView, {HISTORIC}
    from '@/app/core/mixins/RegistryObjectViewHelper'

  import ChangePhaseDialog from './components/ChangePhaseDialog'

  import DomainData from './DomainData'

  import {TYPES, typeProp} from './ApplicationSupport'
  import {LOCKED_STATE} from './constants'

  export default {
    name: 'DomainView',

    components: {
      BaseLayout,
      HistoryPanel,
      OrderType,
      DomainData,
      SimpleCard,
      BackButton,
      RegistryTypeName,
      ChangePhaseDialog,
      StatesDialog
    },

    mixins: [RegistryCommands, RegistryObjectView],

    props: {
      name: {
        type: String,
        default: undefined
      },
      type: typeProp
    },

    data () {
      return {
        TYPES,
        includeRemote: false,
        isLoading: true,
        domain: undefined,
        registryMetadata: {},
        statesDialog: false,
        phaseDialog: false
      }
    },

    computed: {
      ...mapGetters ({
        mayManageObject: 'auth/mayManageObject',
        permissions: 'auth/permissions'
      }),

      domainName () {
        return this.name || this.domain?.name
      },

      loadParams () {
        return {
          id: this.id,
          vid: this.vid,
          name: this.name,
          includeRemote: this.includeRemote
        }
      },

      hasPunycodeName () {
        return this.domain.name !== this.domain.namePunycode
      },

      isModifyDisabled () {
        return this.isUpdateProhibited (this.domain?.states) ||
          !this.supports (this.domain?.registryId, 'DomainModify') ||
          this.domain?.lockStatus === LOCKED_STATE
      },

      isStatusModifyShown () {
        return this.supports (this.domain?.registryId, 'DomainStatusModify')
      },

      isStatusModifyDisabled () {
        return !this.supports (this.domain?.registryId, 'DomainStatusModify') ||
          this.domain?.lockStatus === LOCKED_STATE
      },

      isAdmin () {
        return this.permissions.includes ('ManageAllObjects')
      },

      isThinRegistry () {
        return this.registryMetaData?.thinRegistry
      },

      isInquirable () {
        return this.type === TYPES.DOMAIN && this.domain &&
          !this.domain.deletedVersion && !this.isThinRegistry
      },

      isEditable () {
        return this.type !== TYPES.APPLICATION ||
          this.domain?.status === 'pending'
      },

      isManageable () {
        return this.mayManageObject (this.domain.clientId)
      },

      isSyncable () {
        return this.isInquirable && this.isAdmin && !this.isVirtual (this.domain.registryId)
      }
    },

    watch: {
      loadParams () {
        this.loadDomainByProps ()
      },

      'domain.id' (newValue) {
        if (newValue) {
          this.loadDomainHistoryData ()
        }
      },

      'domain.registryId' (newValue) {
        if (newValue) {
          this.loadRegistryMetaData ()
        }
      }
    },

    created () {
      this.loadDomainByProps ()
    },

    methods: {
      ...mapMutations ({
        displaySuccessMessage: 'notification/setSuccess'
      }),
      ...mapActions ({
        fetchData: 'request/fetchData'
      }),

      isUpdateProhibited,

      /**
       * Load the domain with the given name.
       * The current version of the requested domain will be loaded.
       *
       * @param {Object} params     the object, identifying the domain to load
       */
      loadDomain (params) {
        this.isLoading = true

        const operation = !params.vid || this.type === TYPES.APPLICATION
          ? 'load'
          : 'loadVersion'

        const op = `${this.type}/${operation}`

        this.fetchData ({
          op,
          params: {
            ...params,
            purpose: 'view'
          },
          cb: data => {
            this.domain = {
              ...data.domainData,
              ...data.viewData,
              ...data.domainViewData,
              extraData: data.domainExtraData
            }

            this.setObjectId (this.domain.id)
            const variants = this.domain.extraData.variants

            if (variants?.length) {
              variants.sort ((a, b) => {
                return (`${a.uname}`).localeCompare (b.uname)
              })
            }

            if (params.vid || this.domain.currentVersionDeleted) {
              this.setObjectVersionId (this.domain.versionId)
              this.setActiveTab (HISTORIC)
            }
          },
          cbFinal: ({success}) => {
            if (!success) {
              // if registry data was requested and could not be retrieved
              // (error), then try to load DB version, otherwise do nothing
              // (`this.includeRemote` is already `false` )
              this.includeRemote = false
            }

            this.isLoading = false
          }
        })
      },

      /**
       * Load the registry-specific meta data for the registry with the given
       * ID.
       */
      loadRegistryMetaData () {
        this.fetchData ({
          op: 'registry/loadMetaData',
          params: {
            registryId: this.domain.registryId
          },
          cb: data => {
            this.registryMetadata = data.registryMetaData
          }
        })
      },

      /**
       * Assign the data of the domain version specified by history index
       * to the domain data object.
       *
       * @param {Integer} index         optional index of expanded item to be
       *                                updated
       */
      assignHistoricDomain (index = 0) {
        const domainData = this.historicObjectData[index]
        this.setObjectVersionId (domainData.versionId)

        if (!domainData.domain) {
          domainData.isLoading = true

          const operation = this.type === TYPES.APPLICATION
            ? 'load'
            : 'loadVersion'

          this.fetchData ({
            op: `${this.type}/${operation}`,
            params: {
              vid: domainData.versionId,
              includeRelations: true,
              purpose: 'view'
            },
            cb: data => {
              domainData.domain = {
                ...data.domainData,
                ...data.viewData,
                ...data.domainViewData,
                extraData: data.domainExtraData
              }
            },
            cbFinal: () => {
              domainData.isLoading = false
              this.navigateToCurrentVersion ()
            }
          })
        }
        this.navigateToCurrentVersion ()
      },

      /**
       * Load the historic data of a domain.
       */
      loadDomainHistoryData () {
        return this.fetchData ({
          op: `${this.type}/history`,
          params: {
            id: this.domain.id
          },
          cb: data => {
            this.historicObjectData = data.historyData.map (item => {
              return {...item, isLoading: false, domain: null}
            })

            this.expandHistoryPanelWithLoadedVersion ()
          },
          cbFinal: () => {
            this.isLoading = false
          }
        })
      },

      /**
       * Load the domain specified by component properties.
       */
      loadDomainByProps () {
        this.loadDomain ({...this.loadParams, includeRelations: true})
      },

      /**
       * Synchronize the contact with the registry
       */
      syncDomain () {
        this.fetchData ({
          op: 'domain/sync',
          params: {
            names: [this.domain.name]
          },
          cb: data => {
            this.displaySuccessMessage (this.$tc ('sync.success'))

            if (data.names[this.domain.name].startsWith ('deleted')) {
              this.$router.push ({name: 'domain.search'})
            } else {
              this.domain = undefined
              this.historicObjectData = undefined
              this.loadDomainByProps ()
            }
          }
        })
      }
    }
  }
</script>
